import type { User } from '@/types/user';

/**
 * The only module that touches storage. The key and value shape are the ones the
 * pre-Redux zustand `persist` wrote, so an existing signed-in user survives this
 * migration instead of being silently logged out on their next visit.
 */
const STORAGE_KEY = 'lfour-auth';

export interface StoredAuth {
  token: string | null;
  user: User | null;
}

const EMPTY: StoredAuth = { token: null, user: null };

export function readStoredAuth(): StoredAuth {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return EMPTY;
  try {
    const parsed = JSON.parse(raw) as { state?: Partial<StoredAuth> };
    const state = parsed.state;
    if (!state) return EMPTY;
    return { token: state.token ?? null, user: state.user ?? null };
  } catch {
    // A corrupted blob must not wedge the app on a blank screen forever.
    window.localStorage.removeItem(STORAGE_KEY);
    return EMPTY;
  }
}

export function writeStoredAuth(auth: StoredAuth): void {
  const payload = {
    state: { token: auth.token, user: auth.user, isAuthenticated: Boolean(auth.token) },
    version: 0
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

export function clearStoredAuth(): void {
  window.localStorage.removeItem(STORAGE_KEY);
}

export const getToken = (): string | null => readStoredAuth().token;
